import type { ReactNode } from 'react'
import type { TaxonomyTag } from '@/lib/services/taxonomy-tags/taxonomy-tags.types'
import type { MerchandisingTagGroupingFields } from '@/lib/services/merchandising-tags/merchandising-tags.utils'
import { useIsReadOnly } from '@/lib/casl'
import { useFieldContext } from '@/lib/form-contexts'
import { groupMerchandisingTagOptions } from '@/lib/services/merchandising-tags/merchandising-tags.utils'
import { cn } from '@/lib/utils'
import { CalendarDays, Check, Plus, Tags, X } from 'lucide-react'
import { useId, useState } from 'react'
import { Badge } from '../ui/badge'
import { EmptyState } from '@/components/patterns/empty-state'
import { Button } from '../ui/button'
import { ToggleButton } from '../ui/toggle-button'
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../ui/dialog'
import { Label } from '../ui/label'
import { ScrollArea } from '../ui/scroll-area'
import { Typography } from '@/components/ui/typography'
import FieldInfo from './FieldInfo'
import SearchInput from './SearchInput'

export type OccasionId = string | number
export type OccasionPickerMode = 'multiple' | 'single'
export type OccasionFieldValue<TId extends OccasionId = OccasionId> = TId | TId[] | null | undefined
export type OccasionOption<TId extends OccasionId = OccasionId> = MerchandisingTagGroupingFields &
  Partial<Pick<TaxonomyTag, 'label' | 'sort_order' | 'kind'>> & {
    id: TId
    name?: string
  }
type SelectedOccasionOption<TId extends OccasionId> = Omit<OccasionOption<TId>, 'id'> & {
  id: OccasionFieldValue<TId>
}
interface OccasionPickerCommonProps<TId extends OccasionId> {
  id?: string
  label?: ReactNode
  labelDescription?: ReactNode
  ariaLabel?: string | null
  options?: OccasionOption<TId>[]
  disabled?: boolean
  className?: string
  buttonLabel?: ReactNode
  dialogTitle?: ReactNode
  dialogDescription?: ReactNode
  searchPlaceholder?: string
  selectedPillStyle?: string
  triggerClassName?: string
  applyLabel?: ReactNode
  cancelLabel?: ReactNode
  defaultOpen?: boolean
  placeholder?: ReactNode
  requireSelection?: boolean
  value?: OccasionFieldValue<TId>
  writeToField?: boolean
  onCancel?: () => void
  onDialogClose?: () => void
  renderLayout?: (parts: {
    dialog: ReactNode
    fieldInfo: ReactNode
    label: ReactNode
    selectedSummary: ReactNode
    trigger: ReactNode
  }) => ReactNode
}
export type OccasionPickerProps<TId extends OccasionId = OccasionId> =
  OccasionPickerCommonProps<TId> &
    (
      | { mode?: 'multiple'; onValueChange?: (value: TId[]) => void }
      | { mode: 'single'; onValueChange?: (value: OccasionFieldValue<TId>) => void }
      | { mode: OccasionPickerMode; onValueChange?: (value: OccasionFieldValue<TId>) => void }
    )

const MULTIPLE_MODE = 'multiple'
const SINGLE_MODE = 'single'
const DATE_BACKED_OCCASION_KIND = 'date_backed'

const usesSingleSelection = <TId extends OccasionId>(
  props: OccasionPickerProps<TId>
): props is OccasionPickerCommonProps<TId> & {
  mode: 'single'
  onValueChange?: (value: OccasionFieldValue<TId>) => void
} => props.mode !== MULTIPLE_MODE && props.mode !== undefined

const toOptionKey = (value: OccasionFieldValue) =>
  value === null || typeof value === 'undefined' ? '' : String(value)

const getOptionName = (option: SelectedOccasionOption<OccasionId> | null | undefined) => {
  if (typeof option?.name === 'string') {
    return option.name
  }

  if (typeof option?.label === 'string') {
    return option.label
  }

  return String(option?.id ?? '')
}

const optionMatchesValue = (
  option: SelectedOccasionOption<OccasionId>,
  value: OccasionFieldValue
) => toOptionKey(option?.id) === toOptionKey(value)

const findOption = <TId extends OccasionId>(
  options: OccasionOption<TId>[],
  value: OccasionFieldValue<TId>
) => options.find((option) => optionMatchesValue(option, value))

const isNoOccasionOption = (option: SelectedOccasionOption<OccasionId> | null | undefined) => {
  if (!option) {
    return false
  }

  const optionKey = toOptionKey(option?.id)
  const optionName = getOptionName(option).toLowerCase()

  return optionKey === '' || optionKey === 'none' || optionName === 'no occasion'
}

const sortOptionsByName = <TId extends OccasionId>(options: OccasionOption<TId>[]) =>
  [...options].sort((firstOption, secondOption) => {
    const firstIsNoOccasion = isNoOccasionOption(firstOption)
    const secondIsNoOccasion = isNoOccasionOption(secondOption)

    if (firstIsNoOccasion || secondIsNoOccasion) {
      return Number(secondIsNoOccasion) - Number(firstIsNoOccasion)
    }

    const firstSortOrder = Number(firstOption?.sort_order)
    const secondSortOrder = Number(secondOption?.sort_order)
    const hasFirstSortOrder = Number.isFinite(firstSortOrder)
    const hasSecondSortOrder = Number.isFinite(secondSortOrder)

    if (hasFirstSortOrder && hasSecondSortOrder && firstSortOrder !== secondSortOrder) {
      return firstSortOrder - secondSortOrder
    }

    if (hasFirstSortOrder !== hasSecondSortOrder) {
      return hasFirstSortOrder ? -1 : 1
    }

    return getOptionName(firstOption).localeCompare(getOptionName(secondOption), undefined, {
      sensitivity: 'base',
      numeric: true,
    })
  })

const sortOptionsAlphabetically = <TId extends OccasionId>(options: OccasionOption<TId>[]) =>
  [...options].sort((firstOption, secondOption) =>
    getOptionName(firstOption).localeCompare(getOptionName(secondOption), undefined, {
      sensitivity: 'base',
      numeric: true,
    })
  )

const normalizeMultipleValue = <TId extends OccasionId>(value: OccasionFieldValue<TId>): TId[] =>
  Array.isArray(value) ? value : []

const uniqueValues = <TId extends OccasionId>(values: TId[]) =>
  values.filter(
    (value, index, allValues) =>
      allValues.findIndex((candidate) => toOptionKey(candidate) === toOptionKey(value)) === index
  )

const getSelectedOptions = <TId extends OccasionId>({
  options,
  mode,
  value,
}: {
  options: OccasionOption<TId>[]
  mode: OccasionPickerMode
  value: OccasionFieldValue<TId>
}): SelectedOccasionOption<TId>[] => {
  if (mode === MULTIPLE_MODE) {
    return normalizeMultipleValue(value).map((selectedId) => {
      const option = findOption(options, selectedId)

      return option ?? { id: selectedId, name: 'Selected occasion' }
    })
  }

  if (
    value === null ||
    typeof value === 'undefined' ||
    value === '' ||
    toOptionKey(value) === 'none'
  ) {
    return []
  }

  const option = findOption(options, value)

  if (!option) {
    return [{ id: value, name: 'Selected occasion' }]
  }

  return isNoOccasionOption(option) ? [] : [option]
}

const getInitialDraftValue = <TId extends OccasionId>({
  mode,
  value,
}: {
  mode: OccasionPickerMode
  value: OccasionFieldValue<TId>
}) => (mode === MULTIPLE_MODE ? normalizeMultipleValue(value) : value)

const getSummaryText = ({
  mode,
  selectedOptions,
}: {
  mode: OccasionPickerMode
  selectedOptions: SelectedOccasionOption<OccasionId>[]
}) => {
  if (selectedOptions.length) {
    return null
  }

  return mode === MULTIPLE_MODE ? 'No occasions selected' : 'No occasion selected'
}

const getCountText = ({
  mode,
  selectedCount,
}: {
  mode: OccasionPickerMode
  selectedCount: number
}) => {
  if (selectedCount) {
    return `${selectedCount} occasion${selectedCount === 1 ? '' : 's'} selected`
  }

  return mode === MULTIPLE_MODE ? 'No occasions selected' : 'No occasion selected'
}

const isEmptySingleValue = <TId extends OccasionId>(
  options: OccasionOption<TId>[],
  value: OccasionFieldValue<TId>
) => {
  if (value === null || typeof value === 'undefined' || value === '') {
    return true
  }

  const option = findOption(options, value)

  return toOptionKey(value) === 'none' || isNoOccasionOption(option)
}

const buildOptionGroups = <TId extends OccasionId>({
  options,
}: {
  options: OccasionOption<TId>[]
}) => {
  const selectableOptions = options.filter((option) => !isNoOccasionOption(option))

  return groupMerchandisingTagOptions(selectableOptions).map((group) => ({
    key: group.key,
    title: group.label,
    options:
      group.key === 'everyday'
        ? sortOptionsAlphabetically(group.options)
        : sortOptionsByName(group.options),
  }))
}

function OccasionPicker<TId extends OccasionId>(props: OccasionPickerProps<TId>) {
  const {
    id,
    label = 'Occasions',
    labelDescription = null,
    ariaLabel = null,
    options = [],
    mode = MULTIPLE_MODE,
    disabled = false,
    className = '',
    buttonLabel,
    dialogTitle,
    dialogDescription,
    searchPlaceholder = 'Search occasions...',
    selectedPillStyle = 'classification',
    triggerClassName = '',
    applyLabel,
    cancelLabel = 'Cancel',
    defaultOpen = false,
    placeholder,
    requireSelection = false,
    value,
    writeToField = true,
    onCancel,
    onDialogClose,
    renderLayout,
  } = props
  const field = useFieldContext<OccasionFieldValue<TId>>()
  const { isReadOnly } = useIsReadOnly()
  const generatedId = useId()
  const fieldId = id ?? `${generatedId}-${field.name}`
  const isMultiple = mode === MULTIPLE_MODE
  const resolvedMode = isMultiple ? MULTIPLE_MODE : SINGLE_MODE
  const fieldValue = value ?? field.state.value
  const selectedOptions = getSelectedOptions({
    options,
    mode: resolvedMode,
    value: fieldValue,
  })
  const [isOpen, setIsOpen] = useState(defaultOpen)
  const [search, setSearch] = useState('')
  const [draftValue, setDraftValue] = useState(() =>
    getInitialDraftValue({ mode: resolvedMode, value: fieldValue })
  )
  const summaryText = getSummaryText({ mode: resolvedMode, selectedOptions })
  const normalizedSearch = search.trim().toLowerCase()
  const sortedOptions = sortOptionsByName(options)
  const filteredSortedOptions = normalizedSearch
    ? sortedOptions.filter((option) =>
        getOptionName(option).toLowerCase().includes(normalizedSearch)
      )
    : sortedOptions
  const filteredOptions = filteredSortedOptions
  const isDisabled = isReadOnly || disabled
  const selectedDraftCount =
    resolvedMode === MULTIPLE_MODE
      ? normalizeMultipleValue(draftValue).length
      : isEmptySingleValue(options, draftValue)
        ? 0
        : 1
  const filteredOptionIds = filteredOptions
    .filter((option) => !isNoOccasionOption(option))
    .map((option) => option.id)
  const draftValues = normalizeMultipleValue(draftValue)
  const filteredSelectedCount = filteredOptionIds.filter((id) =>
    draftValues.some((value) => String(value) === String(id))
  ).length
  const hasFilteredOptions = filteredOptionIds.length > 0
  const hasAllVisibleSelection =
    hasFilteredOptions && filteredSelectedCount === filteredOptionIds.length
  const selectAllActionLabel = hasAllVisibleSelection ? 'Select none' : 'Select all'
  const optionGroups = buildOptionGroups({ options: filteredOptions })
  const useNavPills = selectedPillStyle === 'nav'
  const useClassificationPills = selectedPillStyle === 'classification'

  const openPicker = () => {
    setDraftValue(getInitialDraftValue({ mode: resolvedMode, value: fieldValue }))
    setSearch('')
    setIsOpen(true)
  }

  const closePicker = () => {
    setIsOpen(false)
    onDialogClose?.()
  }

  const cancelPicker = () => {
    onCancel?.()
    closePicker()
  }

  const isDraftSelected = (option: OccasionOption<TId>) => {
    if (resolvedMode === MULTIPLE_MODE) {
      return normalizeMultipleValue(draftValue).some((value) => optionMatchesValue(option, value))
    }

    return optionMatchesValue(option, draftValue)
  }

  const setOptionSelected = (option: OccasionOption<TId>, checked: boolean) => {
    if (resolvedMode === MULTIPLE_MODE) {
      setDraftValue((currentValue) => {
        const currentValues = normalizeMultipleValue(currentValue)

        if (checked) {
          return uniqueValues([...currentValues, option.id])
        }

        return currentValues.filter((value) => !optionMatchesValue(option, value))
      })

      return
    }

    setDraftValue(checked ? option.id : null)
  }

  const clearDraftSelection = () => {
    setDraftValue(resolvedMode === MULTIPLE_MODE ? [] : null)
  }

  const applySelection = () => {
    const nextValue =
      resolvedMode === MULTIPLE_MODE
        ? uniqueValues(normalizeMultipleValue(draftValue))
        : isEmptySingleValue(options, draftValue)
          ? null
          : draftValue

    if (writeToField) {
      field.handleChange(nextValue)
    }
    if (usesSingleSelection(props)) {
      props.onValueChange?.(nextValue)
    } else {
      props.onValueChange?.(normalizeMultipleValue(nextValue))
    }
    closePicker()
  }

  const setVisibleSelection = (checked: boolean) => {
    if (!hasFilteredOptions || resolvedMode !== MULTIPLE_MODE) {
      return
    }

    if (checked === true) {
      setDraftValue((currentValue) =>
        uniqueValues([...normalizeMultipleValue(currentValue), ...filteredOptionIds])
      )
      return
    }

    setDraftValue((currentValue) => {
      const visibleIds = new Set(filteredOptionIds.map(String))

      return normalizeMultipleValue(currentValue).filter((id) => !visibleIds.has(String(id)))
    })
  }

  const removeSelectedOption = (option: SelectedOccasionOption<TId>) => {
    if (isDisabled) {
      return
    }

    const nextValue =
      resolvedMode === MULTIPLE_MODE
        ? normalizeMultipleValue(fieldValue).filter((value) => !optionMatchesValue(option, value))
        : null

    if (writeToField) {
      field.handleChange(nextValue)
    }
    if (usesSingleSelection(props)) {
      props.onValueChange?.(nextValue)
    } else {
      props.onValueChange?.(normalizeMultipleValue(nextValue))
    }
  }

  const labelNode = label ? (
    labelDescription ? (
      <div className="min-w-0 space-y-1">
        <Typography variant="h4" className="pb-0">
          {label}
        </Typography>
        <Typography variant="label" tone="muted">
          {labelDescription}
        </Typography>
      </div>
    ) : (
      <Label className="min-w-0">{label}</Label>
    )
  ) : null

  const trigger = !isDisabled ? (
    <Button
      id={fieldId}
      type="button"
      variant="outline"
      size="sm"
      className={cn('shrink-0 px-3.5', triggerClassName)}
      aria-label={ariaLabel ?? undefined}
      onClick={openPicker}
    >
      <Tags className="size-4" />
      {buttonLabel ?? (isMultiple ? 'Choose Occasions' : 'Choose Occasion')}
    </Button>
  ) : null

  const selectedSummary = selectedOptions.length ? (
    <div className="flex flex-wrap gap-2">
      {selectedOptions.map((option) => {
        const isScheduled = option?.kind === DATE_BACKED_OCCASION_KIND
        const Icon = isScheduled ? CalendarDays : Tags
        const optionName = getOptionName(option)

        return (
          <Badge
            key={toOptionKey(option.id)}
            variant={
              useClassificationPills
                ? isScheduled
                  ? 'brand'
                  : 'periwinkle'
                : useNavPills
                  ? 'default'
                  : 'outline'
            }
            size={useClassificationPills ? 'choice' : useNavPills ? 'comfortable' : 'chip'}
          >
            {useClassificationPills ? (
              <Icon className="size-3.5 shrink-0 text-muted-foreground" aria-hidden="true" />
            ) : null}
            <span>{optionName}</span>
            {useClassificationPills && !isDisabled ? (
              <Button
                type="button"
                variant="ghostMuted"
                size="icon-3xs"
                className="-mr-1"
                aria-label={`Remove ${optionName}`}
                onClick={() => removeSelectedOption(option)}
              >
                <X className="size-3" aria-hidden="true" />
              </Button>
            ) : null}
          </Badge>
        )
      })}
    </div>
  ) : (
    <div className={cn(useClassificationPills && 'py-1')}>
      {useClassificationPills ? (
        <Badge variant="placeholder" size="placeholder">
          {placeholder ?? summaryText ?? 'No occasion selected'}
        </Badge>
      ) : (
        <EmptyState
          size="inline"
          surface="transparent"
          title={placeholder ?? summaryText ?? 'No occasion selected'}
        />
      )}
    </div>
  )

  const dialog = (
    <Dialog
      open={isOpen}
      onOpenChange={(nextOpen) => {
        if (nextOpen) {
          setIsOpen(true)
          return
        }

        cancelPicker()
      }}
    >
      {isOpen ? (
        <DialogContent size="large" viewport="contained" padding="none">
          <DialogHeader>
            <div className="px-6 pt-6">
              <DialogTitle>{dialogTitle ?? (isMultiple ? 'Occasions' : 'Occasion')}</DialogTitle>
              {dialogDescription ? (
                <DialogDescription className="mt-1.5">{dialogDescription}</DialogDescription>
              ) : null}
            </div>
          </DialogHeader>

          <div className="space-y-4 px-6 pb-5">
            <SearchInput placeholder={searchPlaceholder} value={search} setValue={setSearch} />

            <div className="space-y-2">
              <div className="flex flex-wrap items-center justify-between gap-3">
                {isMultiple ? (
                  <Button
                    type="button"
                    variant="ghost"
                    size="compact"
                    disabled={!hasFilteredOptions}
                    onClick={() => setVisibleSelection(!hasAllVisibleSelection)}
                  >
                    {selectAllActionLabel}
                  </Button>
                ) : null}
                <div className="ml-auto flex items-center gap-3 text-right">
                  <Typography variant="supporting">
                    {getCountText({
                      mode: resolvedMode,
                      selectedCount: selectedDraftCount,
                    })}
                  </Typography>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="shrink-0"
                    disabled={selectedDraftCount === 0}
                    onClick={clearDraftSelection}
                  >
                    Clear all
                  </Button>
                </div>
              </div>

              <div className="rounded-xl border border-border/80 bg-muted/10">
                <ScrollArea className="h-[min(520px,calc(100dvh-18rem))]">
                  {optionGroups.length ? (
                    <div className="space-y-5 p-4">
                      {optionGroups.map((group) => (
                        <section key={group.key} className="space-y-2.5">
                          <Typography variant="eyebrow" className="block pb-1">
                            {group.title}
                          </Typography>
                          <div className="flex flex-wrap gap-2">
                            {group.options.map((option) => {
                              const isSelected = isDraftSelected(option)
                              const optionName = getOptionName(option)

                              return (
                                <ToggleButton
                                  key={toOptionKey(option.id)}
                                  type="button"
                                  pressed={isSelected}
                                  size="xs"
                                  className="h-auto min-h-9 gap-2 px-3 py-1.5 has-[>svg]:px-3"
                                  onClick={() => setOptionSelected(option, !isSelected)}
                                >
                                  {isSelected ? (
                                    <Check
                                      className="size-3.5 text-interaction"
                                      aria-hidden="true"
                                    />
                                  ) : (
                                    <Plus
                                      className="size-3.5 text-muted-foreground"
                                      aria-hidden="true"
                                    />
                                  )}
                                  <span>{optionName}</span>
                                </ToggleButton>
                              )
                            })}
                          </div>
                        </section>
                      ))}
                    </div>
                  ) : (
                    <div className="flex h-full min-h-[220px] items-center justify-center p-6 text-center">
                      <Typography variant="supporting">
                        No occasions matched that search.
                      </Typography>
                    </div>
                  )}
                </ScrollArea>
              </div>
            </div>

            <div className="flex justify-end gap-3 border-t border-border/80 pt-4">
              <Button type="button" variant="outline" onClick={cancelPicker}>
                {cancelLabel}
              </Button>
              <Button
                type="button"
                disabled={requireSelection && selectedDraftCount === 0}
                onClick={applySelection}
              >
                {applyLabel ?? (isMultiple ? 'Apply Occasions' : 'Apply Occasion')}
              </Button>
            </div>
          </div>
        </DialogContent>
      ) : null}
    </Dialog>
  )

  const fieldInfo = <FieldInfo />

  if (typeof renderLayout === 'function') {
    return renderLayout({
      dialog,
      fieldInfo,
      label: labelNode,
      selectedSummary,
      trigger,
    })
  }

  return (
    <div className={cn('space-y-2', className)}>
      <div className={cn('flex items-center gap-3', label ? 'justify-between' : 'justify-start')}>
        {labelNode}
        {trigger}
      </div>
      {selectedSummary}
      {dialog}
      {fieldInfo}
    </div>
  )
}

export default OccasionPicker
